import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getIsEmailValidationEnabled } from '@app/redux/publicSettings/publicSettings.selectors';

import { Alert, Autocomplete, Grid, TextField, Typography } from '@mui/material';

import EmailPreValidation from '@pages/Auth/RegistrationPage/containers/EmailPreValidation';
import Registration from '@pages/Auth/RegistrationPage/containers/Registration';

import { blocked_countries, countries_list } from '@utils/countries';
import * as environment from '@utils/environment';

const STORAGE_KEY = 'selected_country';

function RegistrationPage() {
	const [selectedCountry, setSelectedCountry] = useState<{ id: number; name: string } | null>(null);
	const [isCountryBlocked, setIsCountryBlocked] = useState(false);
	const [validatedEmail, setValidatedEmail] = useState<string | null>(null);
	const isEmailValidationEnabled = useSelector(getIsEmailValidationEnabled);
	const isLightspeed = environment.env.FIRM_ENVIRONMENT === 'lightspeed';

	useEffect(() => {
		if (!isLightspeed) return;

		const savedCountryId = localStorage.getItem(STORAGE_KEY);
		if (savedCountryId) {
			const country = countries_list.find(c => c.id === Number(savedCountryId));
			if (country) {
				setSelectedCountry(country);
				setIsCountryBlocked(blocked_countries.includes(country.id));
			}
		}
	}, [isLightspeed]);

	const handleCountryChange = (_event: React.SyntheticEvent, value: { id: number; name: string } | null) => {
		setSelectedCountry(value);

		if (value) {
			const isBlocked = blocked_countries.includes(value.id);
			setIsCountryBlocked(isBlocked);
			localStorage.setItem(STORAGE_KEY, value.id.toString());
		} else {
			setIsCountryBlocked(false);
			localStorage.removeItem(STORAGE_KEY);
		}
	};

	const showRegistration = isLightspeed ? selectedCountry && !isCountryBlocked : true;
	const isEmailPreValidationStep = Boolean(showRegistration) && isEmailValidationEnabled && !validatedEmail;

	return (
		<Grid container direction="column" alignItems="center" justifyContent="center">
			<Typography variant="body1" mb={1} fontWeight={500}>
				<b>{`Welcome to ${environment.FIRM_NAME} accounts`}</b>
			</Typography>

			<Typography variant="body2" mb={4} color={'secondary'}>
				{isEmailPreValidationStep
					? 'Please enter your email to continue'
					: 'To register please fill out the information below'}
			</Typography>

			{isLightspeed && !showRegistration && (
				<Grid item sx={{ width: '100%', maxWidth: 400, mb: 3 }}>
					<Autocomplete
						options={countries_list}
						getOptionLabel={option => option.name}
						value={selectedCountry}
						onChange={handleCountryChange}
						renderInput={params => (
							<TextField {...params} label="Select your country of residence" variant="outlined" required />
						)}
					/>

					{isCountryBlocked && selectedCountry && (
						<Alert severity="error" sx={{ mt: 2 }}>
							Unfortunately this broker is not able to service accounts from {selectedCountry.name}. You will not be
							able to open an account.
						</Alert>
					)}
				</Grid>
			)}

			{showRegistration &&
				(isEmailPreValidationStep ? (
					<EmailPreValidation onValidated={setValidatedEmail} />
				) : (
					<Registration validatedEmail={validatedEmail ?? undefined} />
				))}
		</Grid>
	);
}

export default RegistrationPage;
